import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { apiFetch, ApiClientError, getApiFieldError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useSkillLevelSimplified } from '@/hooks/useSkillLevelSimplified';
import { BehaviorDescriptionHint } from '@/components/BehaviorDescriptionHint';
import { deriveSkillLevelFulfillment } from '@/lib/skill-level-comparison';
import { SkillFulfillmentSummary, CriteriaText, FulfillmentIndicator } from '@/components/SkillLevelFulfillment';
import {
  useSkillMatrixRows, type SkillMatrixRow, type SkillMatrixMasterLevel,
} from '@/hooks/useSkillMatrixRows';

/**
 * スキル登録表 (本人視点、mode:'self')。MySkillsPage.tsx 専用 (docs/design/…detail.md §4-9/§8-2)。
 * 「読み取り専用」ではなく「本人視点の表示」— disabled な <select> は使わず、既存 MySkillsPage の
 * プレーンテキスト/バッジ流儀を継承する。目標レベル・目標達成時期のみ、self scope で
 * development_plan:manage 相当の権限が解決された場合にインライン編集可能なチップへ切り替わる。
 *
 * §8-2 [第7版]: 768px以上は表形式 (SkillMatrixEditor.tsx・既存RosterTab.tsxと同じ breakpoint 慣習)、
 * 768px未満は1スキル=1カードのカード形式に切り替える。各水準セルの直下に到達基準文を常時表示し、
 * 「求められる水準」列には充足/不足バッジ・表上部には件数ベースのサマリーを表示する。
 */
export function SkillMatrixSelfViewTable() {
  const qc = useQueryClient();
  const m = useSkillMatrixRows({ mode: 'self' });
  const { isSimplified: skillLevelSimplified } = useSkillLevelSimplified();

  if (m.isLoading) return <div role="status" className="text-text-sub">読み込み中…</div>;
  if (m.isError) return <p role="alert" className="text-sm text-alert-text">スキル・人材像情報の取得に失敗しました。</p>;
  if (m.notLinkedToEmployee) {
    return (
      <div className="border border-surface-border rounded-lg bg-white p-6 space-y-2">
        <p className="text-sm text-text-body">この画面はご本人のスキル・人材像を確認するためのものです。</p>
        <p className="text-xs text-text-sub">
          お使いのアカウントが社員情報と紐付いていないため、確認できる本人を特定できません。
          紐付けは管理者が「社員」画面から行えます。
        </p>
      </div>
    );
  }

  if (m.rows.length === 0) {
    return <EmptyState message="まだ登録されているスキルがありません" description="人材像が割り当てられるとここに表示されます" />;
  }

  const invalidate = () => qc.invalidateQueries({ queryKey: ['development-plans-by-employee', m.employeeId] });

  return (
    <div className="space-y-2">
      {!m.requiredLevelAvailable && (
        <p className="text-xs text-text-sub">
          {m.requiredLevelFeatureDisabled ? 'この機能は現在無効化されています' : 'お使いの権限では人材像が求める水準は表示されません'}
        </p>
      )}

      {m.requiredLevelAvailable && <SkillFulfillmentSummary rows={m.rows} skillLevelsMaster={m.skillLevelsMaster} />}

      {/* ≥768px: 表形式 (SkillMatrixEditor.tsx・RosterTab.tsxと同じ breakpoint 慣習) */}
      <div className="hidden md:block border border-surface-border rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-sunken text-text-sub">
              <tr>
                <th className="text-left px-3 py-2" scope="col">スキル</th>
                {m.requiredLevelAvailable && <th className="text-left px-3 py-2" scope="col">人材像が求める水準</th>}
                {!skillLevelSimplified && <th className="text-left px-3 py-2" scope="col">現状レベル</th>}
                {!m.developmentPlanFeatureDisabled && (
                  <>
                    {!skillLevelSimplified && <th className="text-left px-3 py-2" scope="col">目標レベル</th>}
                    <th className="text-left px-3 py-2" scope="col">目標達成時期</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {m.rows.map((row) => (
                <SelfRow key={row.skillId} row={row} employeeId={m.employeeId!} skillLevelSimplified={skillLevelSimplified}
                  skillLevelsMaster={m.skillLevelsMaster} requiredLevelAvailable={m.requiredLevelAvailable}
                  developmentPlanFeatureDisabled={m.developmentPlanFeatureDisabled}
                  canManageDevelopmentPlan={m.canManageDevelopmentPlan}
                  developmentPlanPermissionResolving={m.developmentPlanPermissionResolving}
                  onChanged={invalidate} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* <768px: 1スキル=1カードの縦積みレイアウト (SkillMatrixEditor.tsx・RosterTab.tsxと同じパターン) */}
      <div className="md:hidden space-y-2">
        {m.rows.map((row) => (
          <SelfCard key={row.skillId} row={row} employeeId={m.employeeId!} skillLevelSimplified={skillLevelSimplified}
            skillLevelsMaster={m.skillLevelsMaster} requiredLevelAvailable={m.requiredLevelAvailable}
            developmentPlanFeatureDisabled={m.developmentPlanFeatureDisabled}
            canManageDevelopmentPlan={m.canManageDevelopmentPlan}
            developmentPlanPermissionResolving={m.developmentPlanPermissionResolving}
            onChanged={invalidate} />
        ))}
      </div>
    </div>
  );
}


function RequiredCell({ row, skillLevelsMaster }: { row: SkillMatrixRow; skillLevelsMaster: SkillMatrixMasterLevel[] }) {
  const label = row.requiredSkillLevelId
    ? (skillLevelsMaster.find((l) => l.skill_level_id === row.requiredSkillLevelId)?.label ?? row.requiredSkillLevelId)
    : null;
  if (!label) return <span className="text-xs text-text-sub">—</span>;
  const fulfillment = deriveSkillLevelFulfillment(row.currentSkillLevelId, row.requiredSkillLevelId, skillLevelsMaster);
  return (
    <div>
      <span className="inline-flex items-center gap-1 text-xs text-text-sub">
        必須 {label}
        {row.requiredBehaviorDescription && <BehaviorDescriptionHint text={row.requiredBehaviorDescription} />}
      </span>
      {fulfillment !== 'unknown' && <div className="mt-1"><FulfillmentIndicator fulfilled={fulfillment === 'fulfilled'} /></div>}
      <CriteriaText text={row.requiredSkillLevelId ? row.levelCriteria[row.requiredSkillLevelId] : undefined} />
    </div>
  );
}

function CurrentLevelCell({ row, skillLevelsMaster }: { row: SkillMatrixRow; skillLevelsMaster: SkillMatrixMasterLevel[] }) {
  const label = row.currentSkillLevelId ? (skillLevelsMaster.find((l) => l.skill_level_id === row.currentSkillLevelId)?.label ?? '—') : '—';
  return (
    <div>
      <span className="text-sm text-text-body">{label}</span>
      <CriteriaText text={row.currentSkillLevelId ? row.levelCriteria[row.currentSkillLevelId] : undefined} />
    </div>
  );
}

/** ≥768px: 表の1行。 */
function SelfRow({
  row, employeeId, skillLevelSimplified, skillLevelsMaster, requiredLevelAvailable,
  developmentPlanFeatureDisabled, canManageDevelopmentPlan, developmentPlanPermissionResolving, onChanged,
}: {
  row: SkillMatrixRow; employeeId: string; skillLevelSimplified: boolean; skillLevelsMaster: SkillMatrixMasterLevel[];
  requiredLevelAvailable: boolean; developmentPlanFeatureDisabled: boolean; canManageDevelopmentPlan: boolean;
  developmentPlanPermissionResolving: boolean; onChanged: () => void;
}) {
  return (
    <tr>
      <td className="px-3 py-2 align-top max-w-[220px]">
        <span className="inline-flex items-start gap-1 flex-wrap break-words">
          {row.skillLabel}
          {row.currentBehaviorDescription && <BehaviorDescriptionHint text={row.currentBehaviorDescription} />}
        </span>
      </td>
      {requiredLevelAvailable && (
        <td className="px-3 py-2 align-top"><RequiredCell row={row} skillLevelsMaster={skillLevelsMaster} /></td>
      )}
      {!skillLevelSimplified && (
        <td className="px-3 py-2 align-top"><CurrentLevelCell row={row} skillLevelsMaster={skillLevelsMaster} /></td>
      )}
      {!developmentPlanFeatureDisabled && (
        <PlanChipCells row={row} employeeId={employeeId} skillLevelSimplified={skillLevelSimplified}
          skillLevelsMaster={skillLevelsMaster} canManage={canManageDevelopmentPlan}
          resolving={developmentPlanPermissionResolving} onChanged={onChanged} variant="table" />
      )}
    </tr>
  );
}

/** <768px: 1スキル=1カード。 */
function SelfCard({
  row, employeeId, skillLevelSimplified, skillLevelsMaster, requiredLevelAvailable,
  developmentPlanFeatureDisabled, canManageDevelopmentPlan, developmentPlanPermissionResolving, onChanged,
}: {
  row: SkillMatrixRow; employeeId: string; skillLevelSimplified: boolean; skillLevelsMaster: SkillMatrixMasterLevel[];
  requiredLevelAvailable: boolean; developmentPlanFeatureDisabled: boolean; canManageDevelopmentPlan: boolean;
  developmentPlanPermissionResolving: boolean; onChanged: () => void;
}) {
  return (
    <div className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
      <div className="text-sm font-semibold text-text-body flex items-start gap-1 flex-wrap break-words">
        {row.skillLabel}
        {row.currentBehaviorDescription && <BehaviorDescriptionHint text={row.currentBehaviorDescription} />}
      </div>
      {requiredLevelAvailable && (
        <div>
          <span className="text-xs text-text-sub">求める水準</span>
          <RequiredCell row={row} skillLevelsMaster={skillLevelsMaster} />
        </div>
      )}
      {!skillLevelSimplified && (
        <div>
          <span className="text-xs text-text-sub">現状レベル</span>
          <CurrentLevelCell row={row} skillLevelsMaster={skillLevelsMaster} />
        </div>
      )}
      {!developmentPlanFeatureDisabled && (
        <PlanChipCells row={row} employeeId={employeeId} skillLevelSimplified={skillLevelSimplified}
          skillLevelsMaster={skillLevelsMaster} canManage={canManageDevelopmentPlan}
          resolving={developmentPlanPermissionResolving} onChanged={onChanged} variant="card" />
      )}
    </div>
  );
}

/** §4-9: 目標レベル・目標達成時期のセル/カード行。resolving中はskeleton、canManage=falseはプレーンテキスト、
 *  canManage=trueは破線チップ (クリックでインライン編集を展開)。 */
function PlanChipCells({
  row, employeeId, skillLevelSimplified, skillLevelsMaster, canManage, resolving, onChanged, variant,
}: {
  row: SkillMatrixRow; employeeId: string; skillLevelSimplified: boolean; skillLevelsMaster: SkillMatrixMasterLevel[];
  canManage: boolean; resolving: boolean; onChanged: () => void; variant: 'table' | 'card';
}) {
  const [editing, setEditing] = useState(false);
  const plan = row.planSelection.kind !== 'none' ? row.planSelection.plan : null;
  const editablePlan = row.planSelection.kind === 'editable';
  const [targetLevelId, setTargetLevelId] = useState(plan?.target_scale_level_id ?? '');
  const [dueDate, setDueDate] = useState(plan?.due_date ?? '');
  const [saving, setSaving] = useState(false);
  // 実装後レビュー MEDIUM是正 (design-and-code、2026-09-20): §4-5「該当行にインラインメッセージ」に
  // 合わせ、グローバルtoastだけでなく行スコープの一時メッセージも表示する
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  const planKey = `${plan?.plan_id ?? 'none'}:${plan?.row_version ?? 0}`;
  const lastKeyRef = useRef(planKey);
  // 実装後レビュー HIGH是正: 目標レベル→期日のように同一行の2フィールドを連続保存すると、
  // 1回目のPUT成功後もqueryの再取得が完了するまでplan.row_versionがpropsで古いまま残り、
  // 2回目のPUTが自分自身の直前の更新と楽観ロック競合してしまう(design-expert
  // 実装後レビュー2026-09-20)。row_versionはrefで即時追従させ、再取得を待たない。
  const rowVersionRef = useRef(plan?.row_version);
  useEffect(() => {
    if (lastKeyRef.current !== planKey) {
      lastKeyRef.current = planKey;
      rowVersionRef.current = plan?.row_version;
      setTargetLevelId(plan?.target_scale_level_id ?? '');
      setDueDate(plan?.due_date ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey]);

  const save = async (patch: { target_scale_level_id?: string; due_date?: string | null }) => {
    setSaving(true);
    setConflictMessage(null);
    try {
      if (editablePlan && plan) {
        await apiFetch(`/development-plans/${plan.plan_id}`, {
          method: 'PUT', body: JSON.stringify({ ...patch, row_version: rowVersionRef.current }),
        });
        rowVersionRef.current = (rowVersionRef.current ?? plan.row_version) + 1;
      } else {
        const nextTargetLevelId = patch.target_scale_level_id ?? targetLevelId;
        if (!nextTargetLevelId) return;
        // §4-4-3: self モードからの書込みは常に明示的に origin:'self' を送信する
        await apiFetch('/development-plans', {
          method: 'POST',
          body: JSON.stringify({
            employee_id: employeeId, skill_id: row.skillId, target_scale_level_id: nextTargetLevelId,
            due_date: (patch.due_date ?? dueDate) || null, origin: 'self',
          }),
        });
      }
      onChanged();
    } catch (e) {
      if (editablePlan && getApiFieldError(e, 'row_version') !== undefined) {
        setConflictMessage('他の変更と競合しました。最新の内容に更新しました');
        setTimeout(() => setConflictMessage(null), 4000);
        onChanged();
      } else if (e instanceof ApiClientError && e.code === 'DUPLICATE_ACTIVE_PLAN') {
        setConflictMessage('既に別の目標が登録されています。最新の内容に更新しました');
        setTimeout(() => setConflictMessage(null), 4000);
        onChanged();
      } else {
        toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました');
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelEditing = () => {
    setTargetLevelId(plan?.target_scale_level_id ?? '');
    setDueDate(plan?.due_date ?? '');
    setEditing(false);
  };

  const levels = skillLevelsMaster.filter((l) => l.scale_id === row.scaleId).sort((a, b) => a.sort_order - b.sort_order);
  const targetLevelLabel = skillLevelsMaster.find((l) => l.skill_level_id === targetLevelId)?.label ?? null;
  const targetCriteriaText = targetLevelId ? row.levelCriteria[targetLevelId] : undefined;

  const wrap = (label: string, node: ReactNode) => (variant === 'table' ? (
    <td className="px-3 py-2 align-top">{node}</td>
  ) : (
    <div>
      <span className="text-xs text-text-sub">{label}</span>
      {node}
    </div>
  ));

  if (resolving) {
    const skeleton = <span className="inline-block h-[22px] w-20 rounded bg-surface-sunken animate-pulse" aria-hidden />;
    return (
      <>
        {!skillLevelSimplified && wrap('目標レベル', skeleton)}
        {wrap('目標達成時期', skeleton)}
      </>
    );
  }

  if (!canManage) {
    return (
      <>
        {!skillLevelSimplified && wrap('目標レベル', (
          <div>
            <span className="text-sm text-text-body">{targetLevelLabel ?? '—'}</span>
            <CriteriaText text={targetCriteriaText} />
          </div>
        ))}
        {wrap('目標達成時期', <span className="text-sm text-text-body">{dueDate || '—'}</span>)}
      </>
    );
  }

  if (!editing) {
    // 破線チップ + 鉛筆アイコン。hover/focus-visible で brand-red (既存 peer パターン、
    // ShiftStandingPatternsPage.tsx:603 / ShiftBoardPage.tsx:968,1133 と同じ用法。§4-9 レビュー反映(第6版))
    const chip = (label: string, ariaLabel: string) => (
      <button type="button" onClick={() => setEditing(true)} aria-label={ariaLabel}
        className="min-h-[44px] inline-flex items-center gap-1 rounded-full border border-dashed border-neutral-line px-2.5 text-xs text-text-body hover:border-brand-red hover:text-brand-red focus-visible:border-brand-red focus-visible:text-brand-red focus-ring">
        {label}<Pencil size={12} aria-hidden />
      </button>
    );
    return (
      <>
        {!skillLevelSimplified && wrap('目標レベル', (
          <div>
            {chip(targetLevelLabel ?? '目標レベルを設定', `${row.skillLabel} 目標レベルを編集`)}
            <CriteriaText text={targetCriteriaText} />
          </div>
        ))}
        {wrap('目標達成時期', chip(dueDate || '目標達成時期を設定', `${row.skillLabel} 目標達成時期を編集`))}
      </>
    );
  }

  // 実装後レビュー MEDIUM是正 (design-expert、2026-09-20): チップ編集の脱出手段がblur以外に無かった。
  // Escapeで編集前の値へ戻して閉じる(明示的なキャンセル手段、Nielsen H3)。
  const onEscape = (e: ReactKeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); cancelEditing(); } };

  return (
    <>
      {!skillLevelSimplified && wrap('目標レベル', (
        <div>
          <Select value={targetLevelId || undefined} disabled={saving}
            onValueChange={(v) => { setTargetLevelId(v); save({ target_scale_level_id: v }); }}>
            <SelectTrigger onKeyDown={onEscape} className="w-full min-w-[120px] min-h-[44px]" aria-label={`${row.skillLabel} 目標レベル`}>
              <SelectValue placeholder="選ぶ" />
            </SelectTrigger>
            <SelectContent>{levels.map((l) => <SelectItem key={l.skill_level_id} value={l.skill_level_id}>{l.label}</SelectItem>)}</SelectContent>
          </Select>
          <CriteriaText text={targetCriteriaText} />
        </div>
      ))}
      {wrap('目標達成時期', (
        <div>
          <input type="date" value={dueDate} disabled={saving || (!editablePlan && !targetLevelId)}
            onChange={(e) => setDueDate(e.target.value)}
            onBlur={() => { save({ due_date: dueDate || null }); setEditing(false); }}
            onKeyDown={onEscape}
            className="w-full min-h-[44px] px-2 rounded border border-surface-border text-sm disabled:opacity-50" />
          {conflictMessage && <p role="status" className="text-[11px] text-alert-text mt-1">{conflictMessage}</p>}
        </div>
      ))}
    </>
  );
}
